import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';

import { useApiMutation } from 'theSource/utils';
import { PSModalWrapper } from 'enrollment/components/Shared/Modal';
import { CheckoutResponseDto, ErrorResponseDto } from 'theSource/types';
import { ROUTES } from 'theSource/app/routes';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanId,
  setEnrollmentContext,
  setEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  useConfigurationQuery,
  GET_ENROLLMENT_CONTEXT_QUERY_KEY,
  GET_BENEFITS_QUERY_KEY,
  useEnrollmentContextQuery,
} from 'theSource/queries';
import { selectIsAdmin } from 'theSource/features';
import {
  GET_DEPENDENTS_QUERY_KEY,
  GET_SUBSCRIBER_DOCUMENTS_QUERY_KEY,
} from 'enrollment/queries';

declare const bootstrap: any;

const CHECKOUT_QUERY_KEY = 'checkout';

interface AdminEarlyCheckoutValuesDto {
  no_confirm_email: boolean;
  life_event_completed: boolean;
  org_plan_year_id: number;
  early_checkout: boolean;
}

interface CheckoutValuesDto {
  enrollment_type: string;
}

export const Confirm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const { configuration } = useConfigurationQuery();
  const location = useLocation();
  const lumosApiHost = configuration?.apiHost;

  const enrollmentContextData = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  const hasPendingLE = enrollmentContextData.enrollmentContext?.data.find(
    (enrollmentContext) =>
      enrollmentContext.enrollment_type_lookup_code === 'life_event' &&
      enrollmentContext?.subscriber_life_event &&
      enrollmentContext?.subscriber_life_event?.org_plan_year_ids.length > 0
  );

  const isAdmin = useSelector(selectIsAdmin);
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const enrollmentSelectedYear = localStorage.getItem('enrollmentSelectedYear');
  const earlyCheckout = location.search.includes('earlyCheckout=true');

  const { isLoading: isCheckingOut, mutate: checkout } = useApiMutation<
    CheckoutResponseDto,
    ErrorResponseDto,
    CheckoutValuesDto | AdminEarlyCheckoutValuesDto
  >({
    queryKey: CHECKOUT_QUERY_KEY,
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/enrollments/checkout`,
    onSuccess: (response) => {
      if (response.data.enroll_complete) {
        queryClient.invalidateQueries(GET_ENROLLMENT_CONTEXT_QUERY_KEY);
        queryClient.invalidateQueries(GET_BENEFITS_QUERY_KEY);
        queryClient.invalidateQueries({
          queryKey: GET_SUBSCRIBER_DOCUMENTS_QUERY_KEY,
        });
        queryClient.invalidateQueries(`${GET_DEPENDENTS_QUERY_KEY}-`);
        queryClient.invalidateQueries(
          `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContext}`
        );
        dispatch(setEnrollmentContext(''));
        navigate(ROUTES.main.home.path);
        dispatch(setEnrollmentOrgPlanYearId(0));
        localStorage.removeItem('enrollmentSelectedYear');
      } else {
        let errors = '<ul class="requirements">';
        response.data.validation_errors?.forEach(
          (validationError) =>
            (errors = errors + `<li>${validationError.message}</li>`)
        );
        errors = errors + '</ul>';
        const dialog = new bootstrap.Modal(
          document.getElementById('checkout-error')
        );
        if (dialog) {
          document.getElementById('error_list')!.innerHTML = errors;
          dialog.show();
        }
      }
    },
    showSuccessSnackbar: false,
  });

  const dismissFunction = () => {
    // admin early checkout if allowed
    if (isAdmin && earlyCheckout && orgPlanYearId) {
      checkout({
        no_confirm_email: true,
        life_event_completed: hasPendingLE ? false : true,
        org_plan_year_id: orgPlanYearId,
        early_checkout: true,
        ignore_plugin_benefits: true,
        enrollment_type: enrollmentContext,
      });
      // subscriber checkout
    } else {
      if (enrollmentContext === 'life_event' && enrollmentSelectedYear) {
        checkout({
          enrollment_type: enrollmentContext,
          life_event_completed: hasPendingLE ? false : true,
          org_plan_year_id: parseInt(enrollmentSelectedYear),
        });
      } else {
        checkout({
          enrollment_type: enrollmentContext,
          life_event_completed: hasPendingLE ? false : true,
        });
      }
    }
  };
  const { t } = useTranslation('pages.enrollment.review');

  return (
    <>
      <PSModalWrapper
        id="confirm-review"
        message={t('modal.description')}
        title={t('modal.title')}
        primaryButton={t('buttons.confirm')}
        primaryActive={!isCheckingOut}
        secondaryButton={t('buttons.cancel')}
        onModalDismiss={dismissFunction}
      ></PSModalWrapper>
      <PSModalWrapper
        id="checkout-error"
        message={`${t(
          'modal.errorDescription'
        )}<p id="error_list" class="mt-3"></p>`}
        title={t('modal.errorTitle')}
        primaryButton={t('common:close')}
      ></PSModalWrapper>
    </>
  );
};

export const Footer = () => {
  const { t } = useTranslation('pages.enrollment.review');
  return (
    <footer
      className="fixed-bottom"
      data-testid="review-bottom-content"
      id="review-bottom-content"
    >
      <ps-button
        id="checkout"
        data-testid="checkout-button"
        label={t('buttons.checkout')}
        level="primary"
        icon-right="arrow-right"
        natural
        other-data={"data-bs-toggle='modal' data-bs-target='#confirm-review'"}
      ></ps-button>
    </footer>
  );
};
